/** Defaults applied when the URL says nothing, matching the API's own. */
export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 20;

/** Upper bound the API enforces; a larger value is a client error there. */
const MAX_PAGE_SIZE = 100;

/**
 * Paging as it appears in the URL.
 */
export interface PaginationSearch {
  page?: number;
  pageSize?: number;
}

/**
 * Narrows one search value to a positive integer, or undefined.
 *
 * Anything a person can type into an address bar arrives here — `page=abc`,
 * `page=0`, `page=-3`, `page=1e9`. All of them fall back to the default rather
 * than reaching the API, because a list that renders an error because someone
 * mistyped a URL is worse than one that shows page one.
 */
function toPositiveInt(value: unknown, max?: number): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  // Number rather than parseInt: parseInt would read '3abc' as 3 and '1.5' as
  // 1, quietly accepting input that was never a page number. Whole positive
  // integers only, everything else falls back.
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return undefined;
  }
  return max ? Math.min(parsed, max) : parsed;
}

/**
 * Validator for a route that pages its list.
 *
 * Spread into a route's own `validateSearch` so paging travels in the URL
 * alongside whatever else that screen accepts.
 */
export function validatePaginationSearch(search: Record<string, unknown>): PaginationSearch {
  return {
    page: toPositiveInt(search.page),
    pageSize: toPositiveInt(search.pageSize, MAX_PAGE_SIZE),
  };
}
